import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLines } from "@/components/site/Motion";
import { useEnquiry } from "@/context/EnquiryContext";
import { COUNTRIES, IMG, ZONES } from "@/data/site";
import { useTrips } from "@/hooks/useTrips";
import { TrustBadges } from "@/components/site/TrustBadges";
import { DestinationPicker } from "@/components/home/DestinationPicker";
import { EImg, T } from "@/context/ContentContext";

const ease = [0.22, 1, 0.36, 1];

const HERO_SLIDES = [
  { country: "Georgia", place: "Gergeti, Kazbegi", image: IMG.kazbegi, pos: "object-top" },
  { country: "Japan", place: "Mount Fuji", image: IMG.japan, pos: "object-center" },
  { country: "Dubai", place: "Burj Khalifa", image: IMG.dubai, pos: "object-center" },
  { country: "Baku", place: "Flame Towers", image: IMG.baku, pos: "object-center" },
];

function RotatingBadge() {
  return (
    <svg viewBox="0 0 120 120" className="spin-slow h-full w-full">
      <defs><path id="circle" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
      <circle cx="60" cy="60" r="58" fill="#C2410C" />
      <text fill="#FBF8F3" style={{ fontFamily: "JetBrains Mono", fontSize: 10.5, letterSpacing: 3 }}>
        <textPath href="#circle">GEORGIA · TBILISI · KAZBEGI · BATUMI ·</textPath>
      </text>
      <path d="M48 72l9-13 5 6.5 3.5-4 9 10.5z" fill="#FBF8F3" />
    </svg>
  );
}

function HeroVisual() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 110, damping: 16 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = HERO_SLIDES.length;
  const go = (d) => { setPaused(true); setIdx((i) => (i + d + n) % n); };
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 4500);
    return () => clearInterval(t);
  }, [paused, n]);
  const slide = HERO_SLIDES[idx];
  const next = HERO_SLIDES[(idx + 1) % n];

  return (
    <div onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1200px] w-full max-w-[460px] mx-auto lg:ml-auto" data-testid="hero-3d-visual">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div initial={{ clipPath: "inset(100% 0% 0% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.4, delay: 0.3, ease }} className="arch overflow-hidden aspect-[3/4] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
          <motion.div drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.18} data-testid="hero-slider"
            onDragStart={() => setPaused(true)}
            onDragEnd={(e, info) => {
              if (info.offset.x < -50 || info.velocity.x < -400) go(1);
              else if (info.offset.x > 50 || info.velocity.x > 400) go(-1);
            }}
            className="relative h-full w-full cursor-grab active:cursor-grabbing touch-pan-y">
            {HERO_SLIDES.map((s, i) => (
              <EImg as={motion.img} k={`home.hero.slide.${i}.img`} editable={i === idx} place="top-1/3 left-1/2 -translate-x-1/2" key={s.country} src={s.image} alt={`${s.place}, ${s.country}`} draggable={false}
                initial={false} animate={{ opacity: i === idx ? 1 : 0, scale: i === idx ? 1 : 1.12 }}
                transition={{ duration: 1.1, ease }}
                className={`pointer-events-none select-none absolute inset-0 h-full w-full object-cover ${s.pos}`} />
            ))}
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/60 to-transparent pointer-events-none" />
          </motion.div>
        </motion.div>
        <motion.button type="button" onClick={() => go(1)} aria-label={`Next: ${next.country}`} data-testid="hero-slide-next"
          style={{ transform: "translateZ(70px)" }} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 1, ease }}
          className="group absolute -left-6 sm:-left-14 bottom-10 w-28 sm:w-40 arch-sm overflow-hidden border-4 border-paper shadow-[0_30px_60px_-25px_rgba(21,17,14,0.6)] aspect-[3/4]">
          {HERO_SLIDES.map((s, i) => (
            <EImg as={motion.img} k={`home.hero.slide.${i}.img`} editable={false} key={s.country} src={s.image} alt="" initial={false} animate={{ opacity: s === next ? 1 : 0 }} transition={{ duration: 0.9 }}
              className={`absolute inset-0 h-full w-full object-cover ${s.pos} transition-transform duration-700 group-hover:scale-110`} />
          ))}
          <span className="absolute inset-x-0 bottom-0 bg-ink/70 text-cream eyebrow text-[10px] py-1.5 text-center">Next · {next.country}</span>
        </motion.button>
        <motion.div style={{ transform: "translateZ(110px)" }} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 1.2, ease }} className="absolute -top-6 -right-3 sm:-right-8 h-24 w-24 sm:h-32 sm:w-32 pointer-events-none">
          <RotatingBadge />
        </motion.div>
        <motion.div style={{ transform: "translateZ(50px)" }} className="absolute right-4 bottom-4 left-24 sm:left-32 flex justify-end pointer-events-none">
          <motion.p key={slide.country} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}
            className="eyebrow bg-ink/70 backdrop-blur-md text-cream rounded-full px-3 py-1.5 text-right" data-testid="hero-slide-caption">
            <T k={`home.hero.slide.${idx}.place`}>{slide.place}</T> · <T k={`home.hero.slide.${idx}.country`}>{slide.country}</T>
          </motion.p>
        </motion.div>
      </motion.div>
      <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-end gap-2" data-testid="hero-slide-tabs">
        {HERO_SLIDES.map((s, i) => (
          <button key={s.country} type="button" onClick={() => { setPaused(true); setIdx(i); }} data-testid={`hero-slide-tab-${s.country.toLowerCase()}`}
            className={`relative rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors duration-300 ${i === idx ? "text-cream border-ink" : "border-ink/15 text-ink/75 hover:border-ink/40"}`}>
            {i === idx && <motion.span layoutId="hero-tab-pill" className="absolute inset-0 rounded-full bg-ink" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
            <span className="relative z-10"><T k={`home.hero.slide.${i}.country`}>{s.country}</T></span>
          </button>
        ))}
        <T k="home.hero.drag" className="eyebrow text-[10px] text-stone-600 ml-1">Drag to explore</T>
      </div>
    </div>
  );
}

function HeroSearch() {
  const navigate = useNavigate();
  const { trips } = useTrips();
  const [dest, setDest] = useState({ name: "Georgia", zone: "cis-caucasus" });
  const go = (e) => {
    e.preventDefault();
    const hasTrips = trips.some((t) => t.destination.toLowerCase() === (dest.name || "").toLowerCase());
    if (!dest.name) navigate("/trips");
    else if (hasTrips) navigate(`/trips?destination=${encodeURIComponent(dest.name)}`);
    else navigate(`/trips?zone=${dest.zone}`);
  };
  return (
    <form onSubmit={go} data-testid="hero-search-form"
      className="flex flex-col sm:flex-row gap-2 sm:items-center rounded-3xl sm:rounded-full bg-white border border-ink/10 p-2 max-w-xl shadow-[0_20px_50px_-25px_rgba(21,17,14,0.35)]">
      <DestinationPicker value={dest} onChange={setDest} />
      <button type="submit" data-testid="hero-search-btn"
        className="group inline-flex items-center justify-center gap-2 rounded-full bg-terra text-paper px-6 py-3.5 font-semibold transition-colors hover:bg-[#a5370a]">
        <T k="home.hero.search">Find trips</T> <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
}

const HERO_CHIPS = ["Georgia", "Armenia", "Azerbaijan", "Kazakhstan", "Uzbekistan", "Dubai", "Bali", "Vietnam", "Japan", "Switzerland"];

function CountryChips() {
  const zoneOf = (n) => COUNTRIES.find(([c]) => c === n)?.[1];
  return (
    <div className="flex flex-wrap gap-2" data-testid="hero-country-chips">
      {HERO_CHIPS.map((n) => (
        <Link key={n} to={n === "Georgia" ? "/georgia" : `/trips?zone=${zoneOf(n)}`} data-testid={`hero-chip-${n.toLowerCase()}`}
          className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors duration-300 ${n === "Georgia" ? "bg-wine text-cream border-wine hover:bg-terra hover:border-terra" : "border-ink/15 text-ink/80 hover:bg-ink hover:text-cream"}`}>
          {n}
        </Link>
      ))}
      <span className="px-2 py-1.5 text-sm text-stone-600">+{COUNTRIES.length - HERO_CHIPS.length} more across {ZONES.length} regions</span>
    </div>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { openEnquiry } = useEnquiry();
  const { trips } = useTrips();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const visualY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const nextGeorgia = trips.find((t) => t.destination === "Georgia" && t.dates?.length && !/request/i.test(t.dates[0]));

  return (
    <section ref={ref} className="relative bg-paper text-ink overflow-hidden min-h-[100svh] pt-32 pb-24 lg:pt-36" data-testid="hero-section">
      <div aria-hidden className="absolute inset-0 pointer-events-none bg-[radial-gradient(60%_50%_at_85%_30%,rgba(233,184,114,0.35),transparent_70%),radial-gradient(45%_40%_at_10%_90%,rgba(242,169,196,0.28),transparent_70%)]" />
      <motion.p aria-hidden style={{ x: wordX }}
        className="absolute bottom-[-0.12em] left-0 font-display font-black tracking-[-0.06em] leading-none text-outline-dark text-[34vw] whitespace-nowrap select-none pointer-events-none">
        Georgia Georgia
      </motion.p>
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="eyebrow text-terra flex flex-wrap items-center gap-3 mb-8">
            <span className="inline-flex items-center gap-3"><span className="relative flex h-2 w-2 shrink-0"><span className="absolute inline-flex h-full w-full rounded-full bg-terra opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-terra" /></span>
            <T k="home.hero.eyebrow">Georgia specialists</T><span className="hidden sm:inline"> · <T k="home.hero.eyebrow2">Group &amp; custom trips</T></span></span>
            <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-blush text-ink px-2.5 py-1 tracking-[0.15em]" data-testid="hero-anniversary-chip"><T k="home.hero.chip">1 year</T></span>
          </motion.p>
          <h1 className="font-display font-extrabold tracking-[-0.045em] leading-[0.9] text-[3.4rem] sm:text-7xl lg:text-[6.6rem]" data-testid="hero-heading">
            <MaskLines lines={[
              <T k="home.hero.title.0">Pack light.</T>,
              <><T k="home.hero.title.1a">Go</T> <T k="home.hero.title.1b" className="font-serif italic font-normal tracking-[-0.02em] text-terra">far.</T></>,
              <><T k="home.hero.title.2a">Begin in</T> <T k="home.hero.title.2b" className="font-serif italic font-normal tracking-[-0.02em] text-wine">Georgia.</T></>,
            ]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75, ease }}
            className="mt-8 max-w-lg text-base sm:text-lg text-stone-600 leading-relaxed">
            <T k="home.hero.intro">Trippy Tastic plans group trips, honeymoons and custom tours across India and abroad, and Georgia is where we know every road.</T>
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease }} className="mt-10 space-y-5">
            <HeroSearch />
            <CountryChips />
            <TrustBadges dark={false} />
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
              <button onClick={() => openEnquiry({ destination: "Georgia" })} data-testid="hero-callback-btn"
                className="font-semibold underline underline-offset-[6px] decoration-terra hover:text-terra transition-colors"><T k="home.hero.callback">Request a callback</T></button>
              {nextGeorgia && (
                <span className="font-mono text-stone-600" data-testid="hero-next-batch">Next Georgia batch · {nextGeorgia.dates[0]}</span>
              )}
            </div>
          </motion.div>
        </div>
        <motion.div style={{ y: visualY }} className="lg:col-span-5 px-6 sm:px-10 lg:px-0">
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
}
